/**
 * Customer account API — talks to the server's /api/customer/* routes.
 * Authentication is a server-side session cookie; no tokens in the browser.
 */
import type { MatSize, MatThickness, MatMaterial, StitchingColor } from '../types'

const BASE = (import.meta.env.VITE_API_URL || '') + '/api/customer'

export class ApiError extends Error {
  status: number
  code?: string
  constructor(message: string, status: number, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

async function call<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApiError(body.error || `Request failed (${res.status})`, res.status, body.code)
  return body as T
}

export interface Customer {
  id: string
  numericId: string
  email: string
  firstName: string
  lastName: string
  displayName: string
  phone: string | null
  acceptsMarketing: boolean
}

export interface CustomerOrderLine {
  title: string
  quantity: number
  variantTitle: string | null
  image: string | null
  total: { amount: string; currencyCode: string }
}

export interface CustomerOrder {
  id: string
  name: string
  orderNumber: number
  processedAt: string
  financialStatus: string | null
  fulfillmentStatus: string | null
  statusUrl: string | null
  total: { amount: string; currencyCode: string }
  lineItems: CustomerOrderLine[]
}

export interface SavedDesignVariant {
  size: MatSize
  thickness: MatThickness
  material: MatMaterial
  stitching: StitchingColor
}

export interface SavedDesignRecord {
  id: string
  name: string
  preview: string // small data-URL preview of the design
  variant: Partial<SavedDesignVariant>
  imageAttrs: { x: number; y: number; scaleX: number; scaleY: number; rotation: number } | null
  createdAt: string
}

/** Full-page redirect to Shopify's hosted sign-in; returns to `returnTo` afterwards. */
export function loginUrl(returnTo = '/account'): string {
  return `${BASE}/login?returnTo=${encodeURIComponent(returnTo)}`
}

/** Full-page redirect that ends both the site session and the Shopify session. */
export function logoutUrl(): string {
  return `${BASE}/logout`
}

export const customerApi = {
  me: () => call<{ customer: Customer | null }>('/me'),
  updateProfile: (input: Partial<Pick<Customer, 'firstName' | 'lastName'>>) =>
    call<{ customer: Customer }>('/me', { method: 'PUT', body: JSON.stringify(input) }),
  orders: () => call<{ orders: CustomerOrder[] }>('/orders'),
  designs: () => call<{ designs: SavedDesignRecord[] }>('/designs'),
  saveDesign: (input: { name: string; preview: string; variant: SavedDesignVariant; imageAttrs: SavedDesignRecord['imageAttrs'] }) =>
    call<{ design: SavedDesignRecord; designs: SavedDesignRecord[] }>('/designs', { method: 'POST', body: JSON.stringify(input) }),
  deleteDesign: (id: string) => call<{ designs: SavedDesignRecord[] }>(`/designs/${encodeURIComponent(id)}`, { method: 'DELETE' }),
}

/**
 * Downscale an image data-URL to a small JPEG preview that fits the
 * per-design storage budget (~56KB). Returns a data URL.
 */
export async function makePreview(src: string, maxWidth = 640, budgetBytes = 56 * 1024): Promise<string> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image()
    i.onload = () => resolve(i)
    i.onerror = reject
    i.src = src
  })
  let width = Math.min(maxWidth, img.naturalWidth)
  for (let attempt = 0; attempt < 6; attempt++) {
    const scale = width / img.naturalWidth
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#111'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    for (const q of [0.8, 0.7, 0.6, 0.5]) {
      const out = canvas.toDataURL('image/jpeg', q)
      if (out.length <= budgetBytes) return out
    }
    width = Math.round(width * 0.7)
  }
  throw new Error('Could not shrink preview enough to save')
}
